/**
 * Input for Farsi text: type normally with system/physical keyboard, or tap the
 * keyboard icon to show the floating Farsi keyboard and type with that.
 * When fixed-bottom or fixed-above-anchor, the keyboard is draggable.
 * Set multiline to render a textarea instead of a single-line input.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Keyboard, GripHorizontal, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FarsiKeyboard } from './FarsiKeyboard';
import { useResponsive } from './MobileResponsive';
import { FLOATING_KEYBOARD_ANIM_MS } from '../../utils/learnerSlideModalMotion';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Pixel bottom inset matching the former CSS `max(…rem, env(safe-area-inset-bottom))`. */
function floatingKeyboardBottomReservePx(banner: { collapsed: boolean; isMobile: boolean }): number {
  const rem =
    typeof document !== 'undefined'
      ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      : 16;
  let safe = 0;
  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('padding-bottom: env(safe-area-inset-bottom)')) {
    const probe = document.createElement('div');
    probe.style.paddingBottom = 'env(safe-area-inset-bottom)';
    probe.style.position = 'fixed';
    probe.style.visibility = 'hidden';
    document.body.appendChild(probe);
    safe = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    document.body.removeChild(probe);
  }
  const base = !banner.collapsed ? 5.5 * rem : 0.75 * rem;
  return Math.max(base, safe);
}

function measureCenteredBottomKeyboardPos(
  kb: HTMLElement,
  banner: { collapsed: boolean; isMobile: boolean }
): { x: number; y: number } {
  const width = kb.offsetWidth;
  const height = kb.offsetHeight;
  const reserve = floatingKeyboardBottomReservePx(banner);
  const x = Math.min(
    Math.max(8, (window.innerWidth - width) / 2),
    Math.max(8, window.innerWidth - width - 8)
  );
  const y = Math.max(8, window.innerHeight - height - reserve);
  return { x, y };
}

type SharedFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement> & React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'rows'
>;

export interface FarsiKeyboardInputProps extends SharedFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Inline below input | fixed near bottom | fixed above the input anchor (search fields). */
  keyboardPosition?: 'below' | 'fixed-bottom' | 'fixed-above-anchor';
  /** Where the keyboard toggle sits relative to the input (default: overlay inside input, right). */
  keyboardTriggerPlacement?: 'inside-right' | 'outside-right';
  /** When true, renders a textarea instead of a single-line input. */
  multiline?: boolean;
  rows?: number;
}

export const FarsiKeyboardInput: React.FC<FarsiKeyboardInputProps> = ({
  value,
  onChange,
  keyboardPosition = 'below',
  keyboardTriggerPlacement = 'inside-right',
  multiline = false,
  rows = 6,
  className = '',
  inputMode,
  readOnly,
  ...fieldProps
}) => {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [keyboardMounted, setKeyboardMounted] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [keyboardPos, setKeyboardPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const keyboardRef = useRef<HTMLDivElement>(null);
  const [bannerUi, setBannerUi] = useState<{ collapsed: boolean; isMobile: boolean }>({
    collapsed: false,
    isMobile: false
  });

  const isFloating =
    keyboardPosition === 'fixed-bottom' || keyboardPosition === 'fixed-above-anchor';

  // Mobile only: suppress OS soft keyboard while the on-screen Farsi keyboard is active.
  // Desktop keeps normal focus/typing so physical keyboards still work.
  const suppressOsKeyboard = showKeyboard && isMobile;
  const fieldReadOnly = suppressOsKeyboard || !!readOnly;
  const fieldInputMode = suppressOsKeyboard ? 'none' : inputMode;

  const finishKeyboardUnmount = useCallback(() => {
    setKeyboardMounted(false);
    setKeyboardPos(null);
  }, []);

  const openKeyboard = useCallback(() => {
    setShowKeyboard(true);
    setKeyboardMounted(true);
    // Always mount hidden; floating modes wait for measured absolute coords before show.
    setKeyboardVisible(false);
    if (prefersReducedMotion() && !isFloating) {
      setKeyboardVisible(true);
    }
  }, [isFloating]);

  // After readOnly/inputMode="none" apply, blur+refocus to dismiss an already-open OS keyboard
  // while keeping caret/selection for floating-key inserts.
  useEffect(() => {
    if (!suppressOsKeyboard) return;
    const el = fieldRef.current;
    if (!el || typeof document === 'undefined') return;
    if (document.activeElement !== el) return;
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    el.blur();
    const id = requestAnimationFrame(() => {
      el.focus({ preventScroll: true });
      try {
        el.setSelectionRange(start, end);
      } catch {
        /* ignore */
      }
    });
    return () => cancelAnimationFrame(id);
  }, [suppressOsKeyboard]);

  useEffect(() => {
    if (!keyboardMounted || !showKeyboard || keyboardVisible) return;

    // Floating modes need absolute coords before show so drag never leaves is-centered.
    if (isFloating && keyboardPos == null) return;

    if (prefersReducedMotion()) {
      setKeyboardVisible(true);
      return;
    }

    const id = requestAnimationFrame(() => setKeyboardVisible(true));
    return () => cancelAnimationFrame(id);
  }, [keyboardMounted, showKeyboard, keyboardVisible, isFloating, keyboardPos]);

  const closeKeyboard = useCallback(() => {
    setShowKeyboard(false);
    if (prefersReducedMotion()) {
      setKeyboardVisible(false);
      finishKeyboardUnmount();
      return;
    }
    setKeyboardVisible(false);
  }, [finishKeyboardUnmount]);

  const handleKeyboardTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (!keyboardVisible && !showKeyboard) {
        finishKeyboardUnmount();
      }
    },
    [keyboardVisible, showKeyboard, finishKeyboardUnmount]
  );

  useEffect(() => {
    if (!keyboardMounted || keyboardVisible || showKeyboard) return;
    const t = window.setTimeout(finishKeyboardUnmount, FLOATING_KEYBOARD_ANIM_MS + 50);
    return () => window.clearTimeout(t);
  }, [keyboardMounted, keyboardVisible, showKeyboard, finishKeyboardUnmount]);

  useEffect(() => {
    const h = (e: Event) => {
      const ce = e as CustomEvent<{ collapsed?: boolean; isMobile?: boolean }>;
      const d = ce.detail;
      setBannerUi({
        collapsed: d?.collapsed ?? false,
        isMobile: d?.isMobile ?? false
      });
    };
    window.addEventListener('app:bottomBannerState', h as EventListener);
    window.dispatchEvent(new CustomEvent('app:requestBottomBannerState'));
    return () => window.removeEventListener('app:bottomBannerState', h as EventListener);
  }, []);

  useEffect(() => {
    if (!isFloating) return;
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (!showKeyboard) return;
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (keyboardRef.current?.contains(t)) return;
      closeKeyboard();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isFloating, closeKeyboard, showKeyboard]);

  useEffect(() => {
    if (!keyboardMounted || keyboardPosition !== 'fixed-above-anchor') return;
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const anchor = containerRef.current;
      const kb = keyboardRef.current;
      if (!anchor || !kb) return;
      const ar = anchor.getBoundingClientRect();
      const kr = kb.getBoundingClientRect();
      const gap = 8;
      const y = Math.max(8, ar.top - kr.height - gap);
      const x = Math.min(
        Math.max(8, ar.left + (ar.width - kr.width) / 2),
        window.innerWidth - kr.width - 8
      );
      setKeyboardPos({ x, y });
    };
    let attempts = 0;
    const tryUntilReady = () => {
      if (cancelled || attempts++ > 45) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          if (!containerRef.current || !keyboardRef.current) {
            tryUntilReady();
            return;
          }
          measure();
        });
      });
    };
    tryUntilReady();
    window.addEventListener('resize', measure);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', measure);
    };
  }, [keyboardMounted, keyboardPosition]);

  // fixed-bottom: measure absolute left/top once before show (same as crossword/search).
  // Never overwrite an existing pos — close sets keyboardVisible false and must not jump back.
  useEffect(() => {
    if (!keyboardMounted || keyboardPosition !== 'fixed-bottom') return;
    let cancelled = false;
    let attempts = 0;
    const tryUntilReady = () => {
      if (cancelled || attempts++ > 45) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          const kb = keyboardRef.current;
          if (!kb || kb.offsetWidth === 0) {
            tryUntilReady();
            return;
          }
          setKeyboardPos((prev) =>
            prev != null ? prev : measureCenteredBottomKeyboardPos(kb, bannerUi)
          );
        });
      });
    };
    tryUntilReady();
    return () => {
      cancelled = true;
    };
  }, [keyboardMounted, keyboardPosition, bannerUi]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    // Prefer already-measured absolute coords; only seed from rect if somehow still null.
    let startLeft = keyboardPos?.x ?? 0;
    let startTop = keyboardPos?.y ?? 0;
    if (keyboardPos == null && keyboardRef.current) {
      const rect = keyboardRef.current.getBoundingClientRect();
      startLeft = rect.left;
      startTop = rect.top;
      setKeyboardPos({ x: startLeft, y: startTop });
    }
    dragRef.current = {
      isDragging: true,
      startX: e.clientX,
      startY: e.clientY,
      startLeft,
      startTop
    };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [keyboardPos]);

  useEffect(() => {
    if (!isFloating) return;
    const onMove = (e: PointerEvent) => {
      if (!dragRef.current.isDragging) return;
      setKeyboardPos({
        x: dragRef.current.startLeft + (e.clientX - dragRef.current.startX),
        y: dragRef.current.startTop + (e.clientY - dragRef.current.startY)
      });
    };
    const onUp = () => {
      dragRef.current.isDragging = false;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [isFloating]);

  const setCursor = (pos: number) => {
    const el = fieldRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      // With inputMode="none" + readOnly while floating KB is open, focus keeps caret
      // without summoning the OS soft keyboard on mobile.
      el.focus({ preventScroll: true });
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        /* ignore */
      }
    });
  };

  const handleInsert = (char: string) => {
    const el = fieldRef.current;
    if (!el) {
      onChange(value + char);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + char + value.slice(end);
    onChange(next);
    setCursor(start + char.length);
  };

  const handleBackspace = () => {
    const el = fieldRef.current;
    if (!el) {
      onChange(value.slice(0, -1));
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    if (start !== end) {
      onChange(value.slice(0, start) + value.slice(end));
      setCursor(start);
      return;
    }
    if (start <= 0) return;
    onChange(value.slice(0, start - 1) + value.slice(start));
    setCursor(start - 1);
  };

  const toggleKeyboard = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (showKeyboard) closeKeyboard();
    else openKeyboard();
  };

  const isMovable = keyboardMounted && isFloating;

  const triggerBtn = (
    <button
      type="button"
      onClick={toggleKeyboard}
      onMouseDown={(e) => e.preventDefault()}
      className={
        keyboardTriggerPlacement === 'outside-right'
          ? 'flex-shrink-0 flex items-center justify-center w-9 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-slate-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/80 border-0 bg-transparent cursor-pointer touch-manipulation'
          : multiline
            ? 'absolute right-0 top-0 flex items-center justify-center w-10 h-10 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-0 bg-transparent cursor-pointer'
            : 'absolute right-0 top-0 bottom-0 flex items-center justify-center w-10 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-0 bg-transparent cursor-pointer'
      }
      title={showKeyboard ? 'Hide Farsi keyboard' : 'Show Farsi keyboard'}
      aria-label={showKeyboard ? 'Hide Farsi keyboard' : 'Show Farsi keyboard'}
    >
      <Keyboard className="w-5 h-5" />
    </button>
  );

  const floatingKeyboardEl =
    keyboardMounted && isFloating ? (
      <div
        ref={keyboardRef}
        className={`fixed z-[220] w-[min(95vw,28rem)] rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 overflow-hidden floating-farsi-keyboard-panel${keyboardVisible ? ' is-visible' : ''}`}
        onTransitionEnd={handleKeyboardTransitionEnd}
        style={
          keyboardPos != null
            ? { left: keyboardPos.x, top: keyboardPos.y }
            : {
                // Pre-measure placeholder: layout size only, never is-centered while interactive.
                left: 0,
                top: 0,
                visibility: 'hidden' as const
              }
        }
      >
        <div className="relative">
          <button
            type="button"
            onClick={closeKeyboard}
            onMouseDown={(e) => e.preventDefault()}
            className="absolute top-1.5 right-1.5 z-10 flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-transform"
            title={t('keyboard.close')}
            aria-label={t('keyboard.close')}
          >
            <X className="w-4 h-4" />
          </button>
          {isMovable && (
            <div
              role="button"
              tabIndex={0}
              onPointerDown={handlePointerDown}
              className="flex items-center justify-center gap-1 py-1.5 px-9 bg-gray-200 dark:bg-gray-700 cursor-grab active:cursor-grabbing select-none border-b border-gray-300 dark:border-gray-600 touch-none"
              title={t('keyboard.dragToMove')}
              aria-label={t('keyboard.dragToMove')}
            >
              <GripHorizontal className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('keyboard.dragToMoveShort')}</span>
            </div>
          )}
          <div className="p-3 pb-[env(safe-area-inset-bottom)]">
            <FarsiKeyboard
              onInsert={handleInsert}
              onBackspace={handleBackspace}
              onClose={closeKeyboard}
            />
          </div>
        </div>
      </div>
    ) : null;

  const inputClass =
    keyboardTriggerPlacement === 'outside-right'
      ? `${className}`.trim()
      : `${className} pr-10`.trim();

  return (
    <div ref={containerRef} className="w-full">
      <div
        className={
          keyboardTriggerPlacement === 'outside-right'
            ? 'relative flex items-stretch w-full gap-1'
            : 'relative flex items-stretch w-full'
        }
      >
        {multiline ? (
          <textarea
            {...(fieldProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
            ref={(el) => {
              fieldRef.current = el;
            }}
            value={value}
            rows={rows}
            readOnly={fieldReadOnly}
            inputMode={fieldInputMode}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        ) : (
          <input
            {...(fieldProps as React.InputHTMLAttributes<HTMLInputElement>)}
            ref={(el) => {
              fieldRef.current = el;
            }}
            value={value}
            readOnly={fieldReadOnly}
            inputMode={fieldInputMode}
            onChange={(e) => onChange(e.target.value)}
            className={inputClass}
          />
        )}
        {triggerBtn}
      </div>
      {keyboardMounted &&
        isFloating &&
        typeof document !== 'undefined' &&
        createPortal(floatingKeyboardEl, document.body)}
      {showKeyboard && !isFloating && (
        <div className="mt-2">
          <div className="p-3 pb-[env(safe-area-inset-bottom)]">
            <FarsiKeyboard onInsert={handleInsert} onBackspace={handleBackspace} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FarsiKeyboardInput;
